import { Editor } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import axios from "axios";
import { useEffect } from "react";
import { BurstTracker, TypingBurst } from "../services/typingSpeed/burstTracker";

const FLUSH_MS = 60_000;
const MAX_BUFFER = 50;
const pluginKey = new PluginKey("typingSpeed");

/**
 * Measures how fast the officer types in the case editor and reports it to
 * POST /api/settings/typing-samples (characters and milliseconds only). Only
 * real keystrokes count: `handleTextInput` does not fire for pastes, AI
 * drafts, dictation inserts or any other programmatic change. Failures are
 * dropped silently, since this is a statistic and never worth interrupting
 * someone mid-report.
 */
export function useTypingSpeed(editor: Editor | null) {
  useEffect(() => {
    if (!editor) return;
    let buffer: TypingBurst[] = [];

    const flush = () => {
      if (buffer.length === 0) return;
      const samples = buffer.slice(0, MAX_BUFFER);
      buffer = buffer.slice(MAX_BUFFER);
      axios.post("/api/settings/typing-samples", { samples }).catch(() => undefined);
    };

    const tracker = new BurstTracker((b) => {
      buffer.push(b);
      if (buffer.length >= MAX_BUFFER) flush();
    });

    editor.registerPlugin(
      new Plugin({
        key: pluginKey,
        props: {
          handleTextInput(view, _from, _to, text) {
            if (view.editable) tracker.input(text.length, Date.now());
            return false; // never consume the input
          },
        },
      }),
    );

    const onHide = () => {
      if (document.visibilityState === "hidden") {
        tracker.finish();
        flush();
      }
    };
    document.addEventListener("visibilitychange", onHide);
    const timer = window.setInterval(() => {
      tracker.finish();
      flush();
    }, FLUSH_MS);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onHide);
      tracker.finish();
      flush();
      if (!editor.isDestroyed) editor.unregisterPlugin(pluginKey);
    };
  }, [editor]);
}
